import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { currentMonthSP, formatMonthLabel, formatYm, parseYm } from '@lira/shared';
import { useMonthStore } from '@/stores/month';
import { cn } from '@/lib/cn';

const EASE = [0.16, 1, 0.3, 1] as const;

const MONTHS_ABBR = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
] as const;

/**
 * Seletor de mês global (pill outline roxa). As setas avançam/voltam um mês;
 * clicar no rótulo abre uma grade de meses (com navegação de ano) para pular
 * direto para qualquer mês — como o seletor de mês de um calendário.
 */
export function MonthSelector({ className }: { className?: string }) {
  const month = useMonthStore((s) => s.month);
  const step = useMonthStore((s) => s.step);
  const setMonth = useMonthStore((s) => s.setMonth);
  const goToday = useMonthStore((s) => s.goToday);

  const { year: selYear, month: selMonth } = parseYm(month);
  const { year: todayYear, month: todayMonth } = parseYm(currentMonthSP());

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selYear);
  const wrapRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const openPicker = () => {
    setViewYear(selYear); // sempre abre no ano do mês selecionado
    setOpen(true);
  };

  // fecha ao clicar fora e com Escape
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // ao abrir, foca o mês selecionado (ou o 1º) para navegação por teclado
  useEffect(() => {
    if (!open) return;
    const grid = gridRef.current;
    const target =
      grid?.querySelector<HTMLButtonElement>('button[data-selected="true"]') ??
      grid?.querySelector<HTMLButtonElement>('button[data-m]');
    target?.focus();
  }, [open]);

  const pick = (monthIndex: number) => {
    setMonth(formatYm(viewYear, monthIndex + 1));
    setOpen(false);
  };

  // setas do teclado movem o foco na grade 3×4 (sem atravessar de ano; use ‹ › do ano)
  const onGridKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      gridRef.current?.querySelectorAll<HTMLButtonElement>('button[data-m]') ?? [],
    );
    const idx = buttons.findIndex((b) => b === document.activeElement);
    if (idx < 0) return;
    const delta =
      e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? 3 : e.key === 'ArrowUp' ? -3 : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next = idx + delta;
    if (next >= 0 && next < buttons.length) buttons[next]?.focus();
  };

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <div className="inline-flex items-center rounded-full border border-primary/30 bg-surface p-0.5">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label="Mês anterior"
          className="grid size-11 place-items-center rounded-full text-primary transition-colors hover:bg-primary-weak"
        >
          <ChevronLeft className="size-5" />
        </button>
        <button
          type="button"
          onClick={() => (open ? setOpen(false) : openPicker())}
          aria-haspopup="dialog"
          aria-expanded={open}
          title="Escolher mês"
          className="flex h-11 min-w-[132px] items-center justify-center gap-1 px-2 text-sm font-semibold text-ink"
        >
          {formatMonthLabel(month)}
          <ChevronDown
            className={cn('size-4 text-primary transition-transform', open && 'rotate-180')}
          />
        </button>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label="Próximo mês"
          className="grid size-11 place-items-center rounded-full text-primary transition-colors hover:bg-primary-weak"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: EASE }}
            role="dialog"
            aria-label="Selecionar mês"
            className="absolute left-1/2 top-full z-40 mt-2 w-[264px] max-w-[calc(100vw-1rem)] -translate-x-1/2 rounded-lg border border-border bg-surface p-3 shadow-hover"
          >
            {/* navegação de ano */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewYear((y) => y - 1)}
                aria-label="Ano anterior"
                className="grid size-11 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
              >
                <ChevronLeft className="size-5" />
              </button>
              <span className="text-sm font-bold text-ink" aria-live="polite">
                {viewYear}
              </span>
              <button
                type="button"
                onClick={() => setViewYear((y) => y + 1)}
                aria-label="Próximo ano"
                className="grid size-11 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
              >
                <ChevronRight className="size-5" />
              </button>
            </div>

            {/* grade dos 12 meses */}
            <div ref={gridRef} onKeyDown={onGridKeyDown} className="mt-2 grid grid-cols-3 gap-1">
              {MONTHS_ABBR.map((abbr, i) => {
                const isSelected = viewYear === selYear && i + 1 === selMonth;
                const isToday = viewYear === todayYear && i + 1 === todayMonth;
                return (
                  <button
                    key={abbr}
                    type="button"
                    data-m={i}
                    data-selected={isSelected || undefined}
                    aria-current={isSelected ? 'date' : undefined}
                    aria-label={formatMonthLabel(formatYm(viewYear, i + 1))}
                    onClick={() => pick(i)}
                    className={cn(
                      'min-h-11 rounded-lg px-2 py-2 text-sm font-medium transition-colors',
                      isSelected
                        ? 'bg-primary text-white'
                        : isToday
                          ? 'text-primary ring-1 ring-inset ring-primary/40 hover:bg-primary-weak'
                          : 'text-ink hover:bg-canvas',
                    )}
                  >
                    {abbr}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => {
                goToday();
                setOpen(false);
              }}
              className="mt-2 min-h-11 w-full rounded-lg px-2 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary-weak"
            >
              Ir para o mês atual
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
